<script setup lang="ts">
import { savedViewsRegistry } from '@/modules/saved-views/store/registry';
import SavedViewCard from './SavedViewCard.vue';
import { useNoReactive } from '@/composables/reactivity';

const props = defineProps<{
  name: string;
}>();

const name = useNoReactive(() => props.name, 'Saved views name should never change');

const savedViewsStore = savedViewsRegistry[name]();
</script>

<template>
  <div class="saved-views-manager" :class="{ 'is-expanded': savedViewsStore.isSavedViewsExpanded }">
    <div class="d-flex flex-column" style="height: 100%">
      <div class="flex-shrink-0">
        <div class="d-flex align-center justify-space-between ml-4 mt-2 mb-1 mr-4">
          <p class="mb-0">Saved Views</p>
          <div class="d-flex align-center gap-1">
            <VTooltip v-if="savedViewsStore.hasSavedViews" bottom open-delay="300">
              <template #activator="{ on, attrs }">
                <VBtn
                  v-bind="attrs"
                  data-test="tab-toggle-btn"
                  icon
                  small
                  style="opacity: 0.7"
                  v-on="on"
                  @click="savedViewsStore.toggleTabs"
                >
                  <VIcon>mdi-tab</VIcon>
                </VBtn>
              </template>
              <span>{{ savedViewsStore.showTabs ? 'Hide tabs' : 'Show tabs' }}</span>
            </VTooltip>
            <VTooltip bottom open-delay="300">
              <template #activator="{ on, attrs }">
                <VBtn
                  v-bind="attrs"
                  color="primary"
                  data-test="create-view-btn"
                  :disabled="savedViewsStore.isCreating"
                  icon
                  small
                  v-on="on"
                  @click="savedViewsStore.addNewView"
                >
                  <VIcon>mdi-plus</VIcon>
                </VBtn>
              </template>
              <span>Create new view</span>
            </VTooltip>
          </div>
        </div>
      </div>

      <div class="flex-grow-1 overflow-y-auto">
        <VContainer class="pa-2" fluid>
          <div
            v-if="savedViewsStore.savedViews.length === 0"
            class="empty-state mt-8 text-center pa-4"
            data-test="empty-state"
          >
            <VIcon class="mb-3" color="grey" size="48">mdi-bookmark-outline</VIcon>
            <p class="grey--text mb-3">No saved views yet</p>
            <p class="grey--text text-caption mb-4">
              Save the current filtering state for quick access. You can share it with your
              colleagues or keep it private.
            </p>
            <VBtn
              color="primary"
              data-test="create-first-view-btn"
              outlined
              small
              @click="savedViewsStore.addNewView"
            >
              <VIcon left small>mdi-plus</VIcon>
              Create First View
            </VBtn>
          </div>

          <SavedViewCard
            v-for="(view, index) in savedViewsStore.savedViews"
            :key="index"
            :index="index"
            :name="name"
            :view="view"
          />
        </VContainer>
      </div>

      <div class="text-center mb-4 mt-2">
        <VTooltip left open-delay="300">
          <template #activator="{ on, attrs }">
            <VBtn
              v-bind="attrs"
              data-test="saved-views-mini-variant-toggler"
              elevation="2"
              fab
              small
              v-on="on"
              @click="savedViewsStore.toggleSavedViewsExpanded"
            >
              <VIcon>{{
                savedViewsStore.isSavedViewsExpanded
                  ? 'mdi-chevron-double-right'
                  : 'mdi-chevron-double-left'
              }}</VIcon>
            </VBtn>
          </template>
          <span>Collapse <strong>]</strong></span>
        </VTooltip>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.saved-views-manager {
  width: 0;
  height: 0;
  background-color: #1e1e1e;
  transition:
    width 0.1s ease,
    border-left-width 0.1s ease;
  overflow: hidden;
  white-space: nowrap;

  &.is-expanded {
    width: 320px;
    height: 100%;
    border-left-width: 1px;
  }
}

.empty-state {
  white-space: normal;
}

.gap-1 {
  gap: 0.25rem;
}
</style>
